<template>
  <sidebar-layout
    :show-leaderboard-ad="loading === 0"
  >
    <h1
      class="heading--with-divider"
      data-test-id="search-page-heading"
    >Search</h1>
    <condition-select
      v-if="showConditionSelect"
      class="condition-select--match-search-bar"
      :click-handler-for-options="goToConditionPage"
    />
    <search-form
      v-else
      :term="query"
      @search="search"
    />
    <select-search-type v-if="siteHasFeature('rare_disease')" @changed="handleSearchTypeSelect" />
    <template v-if="showConditionSelect">
      <p>
        Once you find and select your condition, you will be taken directly to the
        condition overview page.
      </p>
    </template>
    <template v-else>
      <disorder-teaser v-if="resultsIncludeDisorderTeaser" :disorder="disorderTeaser" />
      <filter-by-condition
        v-if="showConditionFilter"
        @conditionSelected="onConditionSelect"
        @conditionCleared="clearCondition"
      />
      <div v-if="loading === 0">
        <p>
          Displaying {{ totalCount }} results for <span class="search-page__search-term">"{{ query }}"</span>
        </p>
        <paginated-list
          :items="resultsMinusDisorderTeaser"
          :current-page="page"
          :total-count="totalCount"
        />
      </div>
      <loading-spinner v-else />
    </template>
    <template v-slot:sidebar-ads>
      <sidebar-ads v-if="loading === 0" :sections="sidebarAdSections" />
    </template>
  </sidebar-layout>
</template>

<script>
import { SEARCH_QUERY } from '@/graphql/queries/search-queries';
import SearchForm from '@/components/Search/SearchForm.vue';
import PaginatedList from '@/components/Pagination/PaginatedList.vue';
import SidebarAds from '@/components/Ads/SidebarAds.vue';
import { config } from '@/configuration/nexus-config';
import routeMixin from '@/mixins/route-mixin';
import FilterByCondition from '@/components/Disorder/FilterByCondition.vue';
import SelectSearchType from '@/components/Disorder/SelectSearchType.vue';
import { mapGetters } from 'vuex';
import ConditionSelect from '@/components/Disorder/ConditionSelect.vue';
import DisorderTeaser from '@/components/Disorder/DisorderTeaser.vue';

export default {
  name: 'SearchPage',
  components: {
    SearchForm,
    PaginatedList,
    SidebarAds,
    FilterByCondition,
    SelectSearchType,
    ConditionSelect,
    DisorderTeaser,
  },
  mixins: [routeMixin],
  data() {
    return {
      results: [],
      dataLayer: {},
      totalCount: 0,
      loading: 0,
      sidebarAdSections: config.sidebarAdSections,
      conditionSlug: this.$route.query.con,
      searchType: 'entire site',
    };
  },
  metaInfo() {
    return {
      title: 'Search',
      meta: [
        {
          name: 'robots',
          vmid: 'robots',
          content: 'noindex',
        },
      ],
    };
  },
  computed: {
    ...mapGetters(['siteHasFeature']),
    query() {
      return this.$route.query.s;
    },
    page() {
      return (this.$route.query.page && parseInt(this.$route.query.page, 10)) || 1;
    },
    showConditionFilter() {
      return this.siteHasFeature('rare_disease') && !this.resultsIncludeDisorderTeaser;
    },
    resultsIncludeDisorderTeaser() {
      return this.results[0]?.type === 'disorder';
    },
    resultsMinusDisorderTeaser() {
      if (this.resultsIncludeDisorderTeaser) return this.results.slice(1);

      return this.results;
    },
    disorderTeaser() {
      if (!this.resultsIncludeDisorderTeaser) return null;

      return this.results[0];
    },
    showConditionSelect() {
      return this.searchType === 'condition';
    },
  },
  watch: {
    results(to) {
      if (!to) { return; }
      this.onLoad();
    },
  },
  updated() {
    const searchPayload = {
      search_terms: [this.$route.query.s],
      filter: {
        search_type: 'site search',
      },
      totalResults: this.totalCount,
      firstPageResults: this.totalCount < 20 ? this.totalCount : 20,
    };
    this.$snowplow.trackSiteSearch(searchPayload);
  },
  methods: {
    search(term, condition) {
      const query = { s: term };
      if (!condition && this.conditionSlug) {
        query.con = this.conditionSlug;
      } else if (condition) {
        query.con = condition;
      }

      this.$router.push({ name: 'search', query });
    },
    onLoad() {
      this.$store.dispatch('updateGlobalContext', {
        search: {
          type: 'Content Search',
          query: this.results.map((r) => r.id),
        },
        featuredContent: {
          location: 'Content Search',
          ids: this.results.filter((r) => r.isFeatured).map((r) => r.id),
        },
      });
      this.$emit('loaded', this.dataLayer);
    },
    onConditionSelect(disorder) {
      this.search(this.query, disorder.slug);
    },
    clearCondition() {
      this.condition = null;
      this.$router.push({ name: 'search', query: { s: this.$route.query.s } });
    },
    handleSearchTypeSelect(searchType) {
      this.searchType = searchType;
    },
    goToConditionPage(event, disorder) {
      this.$router.push(`conditions/${disorder.slug}`);
    },
  },
  apollo: {
    search: {
      query: SEARCH_QUERY,
      variables() {
        return {
          page: this.page,
          query: this.query,
          tagSlug: this.conditionSlug ? `con-${this.conditionSlug}` : null,
          postTypes: ['page', 'post', 'cas', 'story', 'thread', 'recipe', 'sponsored_article', 'quiz'],
        };
      },
      result({ data }) {
        if (data) {
          if (!data.search || !data.search.searchResults) { return; }
          this.dataLayer = data.search.dataLayer;
          this.totalCount = data.search.total;
          this.results = data.search.searchResults;
        } else {
          this.results = [];
        }
      },
    },
  },
};
</script>

<style lang="scss"> // no scoped - to reach html content
  @import '@/stylesheets/legacy/_highlight';
  @import '@/stylesheets/components/_search-page';
</style>
